import { Button, Flex, Space, Switch } from "antd";
import { isArray } from "lodash-es";
import { FormattedMessage, useIntl } from "react-intl";
import PermissionCheck from "~Common/PermissionCheck";
import { DataTableColumnChooser } from "~components/DataTable/components/DataTableColumnChooser/DataTableColumnChooser.tsx";
import { FilterPanel } from "~components/DataTable/components/FilterPanel/FilterPanel.tsx";
import { SecondaryFilterLabel } from "~components/DataTable/components/FilterPanel/SecondaryFilterLabel.tsx";
import { useTableFilters } from "~components/DataTable/hooks/useTableFilters.ts";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { FiltersConfig } from "~components/DataTable/types/filtersConfig.ts";
import type { SearchConfig } from "~components/DataTable/types/searchConfig.ts";
import SearchFilter from "~components/SearchFilter/SearchFilter.tsx";
import { type PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import type { DataTableConfig } from "~types/dataTableConfig.ts";

type DataTableHeaderProps<T = any> = {
  filtersConfig?: FiltersConfig;
  searchConfig?: SearchConfig;
  handleAdd?: () => void;
  permissionNames: Array<PermissionNames>;
  tableColumns: DataTableColumn<T>[];
  setSelectedColumns: (columns: Array<string>) => void;
  columnChooserLocalStorageKey: string;
  dataTableUiConfiguration: DataTableConfig;
  searchPlaceholderIntlKey?: string;
  onMassEditModeChangedHandler?: (checked: boolean) => void;
  massEditMode?: boolean;
  handleRefresh?: () => void;
  isRefreshing: boolean;
};

export const DataTableHeader = <T = any>({
  filtersConfig,
  searchConfig,
  searchPlaceholderIntlKey,
  permissionNames,
  onMassEditModeChangedHandler,
  massEditMode,
  handleRefresh,
  tableColumns,
  setSelectedColumns,
  columnChooserLocalStorageKey,
  dataTableUiConfiguration,
  isRefreshing,
}: DataTableHeaderProps<T>) => {
  const { formatMessage } = useIntl();
  const { currentFilter } = filtersConfig ?? {};

  const uiFilterConfig = dataTableUiConfiguration.filtersConfig || [];

  const { primaryFilters, secondaryFilters, onFilterChange } = useTableFilters(uiFilterConfig, filtersConfig);
  const hasFilterPanel = secondaryFilters && secondaryFilters.length > 0;

  return (
    <div className="data-table-header">
      <div className={`data-table-header-grid ${hasFilterPanel ? "has-filter-panel" : ""}`}>
        <Flex gap={10} className="filter-panel" align="center" wrap="wrap">
          {hasFilterPanel && (
            <FilterPanel filters={secondaryFilters} onChangeFilters={onFilterChange} currentFilter={currentFilter || {}} />
          )}
          {secondaryFilters.map((filter) => {
            const { filterKey, secondaryFilterLabel } = filter;
            const filterValue = currentFilter?.[filterKey];
            return (
              filterValue &&
              !(isArray(filterValue) && filterValue.length === 0) && (
                <Flex key={filterKey} className="filter-panel-label">
                  {secondaryFilterLabel ? secondaryFilterLabel : <SecondaryFilterLabel value={filterValue} />}
                  <Button
                    type="text"
                    size={"small"}
                    icon={<i className="fas fa-x" style={{ fontSize: "8px" }} />}
                    className="data-table-filter-panel-clear-button"
                    onClick={() => {
                      onFilterChange(filterKey, undefined);
                    }}
                  />
                </Flex>
              )
            );
          })}
        </Flex>
        <div className="data-table-filters">
          {primaryFilters.map(({ filterKey, filterElement }) => (
            <div key={filterKey}>
              {filterElement({
                value: currentFilter?.[filterKey],
                onChange: (values: string[]) => onFilterChange(filterKey, values),
              })}
            </div>
          ))}
        </div>
        <div className="data-table-controls">
          <div className="action-buttons-wrapper">
            <div className="search-wrapper">
              {searchConfig && (
                <SearchFilter
                  searchValue={searchConfig.searchText}
                  doSearch={searchConfig?.handleSearch}
                  placeholderIntlKey={searchPlaceholderIntlKey || "search.two.letters"}
                />
              )}
            </div>
            {onMassEditModeChangedHandler && (
              <PermissionCheck permissionKeys={permissionNames} permissionOptions={[PermissionOperations.UPDATE]}>
                <Space>
                  {formatMessage({ id: "edit.mode" })}
                  <Switch checked={massEditMode} onChange={onMassEditModeChangedHandler} />
                </Space>
              </PermissionCheck>
            )}
            {handleRefresh && (
              <Button disabled={isRefreshing} onClick={handleRefresh} type="text" className="table-header-button">
                <i className="fa-solid fa-arrows-rotate"></i>
                <FormattedMessage id="data.table.button.refresh" />
              </Button>
            )}
            <DataTableColumnChooser
              setSelectedColumns={setSelectedColumns}
              tableConfiguration={dataTableUiConfiguration.columnsConfig}
              localStorageKey={columnChooserLocalStorageKey}
              columns={tableColumns.map(
                (x) =>
                  ({ key: x.key || x.dataIndex, title: x.title }) as {
                    key: string;
                    title: string;
                  },
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
